// Features: M015-F0032, M004-F0702 (WebUI #52). Tests: T-CI-WEB-TEACHER-LEDGER-001 cases A–E, T-M004-0180, T-M004-0182.
// Design authority: docs/design/M015-F0032-Teacher-Ledger-Page.md
// Pending (cost-due, not yet billed) = the caller's OWN unbilled occurrences from
// Core's own pipeline projection (M004-F0702; Core-local, no tunnel, so it does not
// depend on the ledger node being up). Billed history is read from the ledger
// through Core's ledger.read gateway. Core never writes bills; the page never
// computes a price. The ledger
// node being offline (HTTP 503 LEDGER_NODE_OFFLINE / LEDGER_HA_READ_ONLY) is a
// rendered state — never an error page, never an unbounded spinner.

import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../layout/Layout';
import { useAuth } from '../../context/AuthContext';
import { ledgerAccountApi, organisationApi, userApi } from '../../services/api';
import TxChip from '../TxChip';
import {
  PENDING_PAGE, PENDING_MAX, nextLimit, normalisePending, pendingAmount, pendingDescription, pendingWhen, stageWords, stateWords,
} from './ledgerPendingModel';
import './TeacherLedgerPage.css';

const payload = (response) => response?.data?.data || response?.data || response || {};
const organisationId = (organisation) => Number(organisation?.id || organisation?.organisation_id || 0);
const organisationName = (organisation) => organisation?.name || organisation?.organisation_name || `Organisation ${organisationId(organisation)}`;
const money = (value, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value || 0));
const apiMessage = (cause) => cause?.response?.data?.error?.message || cause?.response?.data?.message || cause?.message || '';
const entryDate = (entry) => (entry?.created_at ? new Date(entry.created_at).toLocaleDateString() : '—');

// Core's ledger gateway answers HTTP 503 exactly when the ledger node cannot
// serve reads: LEDGER_NODE_OFFLINE (active leader offline) and
// LEDGER_HA_READ_ONLY (no safe active leader). A bare 503 means the same thing.
export const isLedgerOfflineError = (cause) => Number(cause?.response?.status) === 503;

export function selectTeacherLedgerOrganisations(organisationResponse, accessResponse) {
  const organisationPayload = payload(organisationResponse);
  const organisations = Array.isArray(organisationPayload)
    ? organisationPayload
    : Array.isArray(organisationPayload.organisations)
      ? organisationPayload.organisations
      : Array.isArray(organisationPayload.items) ? organisationPayload.items : [];
  const ledgerIds = new Set(((payload(accessResponse)?.applications || {}).ledger?.organisationIds || []).map(Number));
  return organisations
    .filter((organisation) => ledgerIds.has(organisationId(organisation)))
    .map((organisation) => ({ id: organisationId(organisation), name: organisationName(organisation) }));
}

// M004-F0702 — the caller's own pending list, exactly as Core served it.
function PendingList({ pending, hasOrganisation, onRetry, onShowMore }) {
  if (!hasOrganisation) {
    return (
      <div className="teacher-ledger-state" role="status">
        <h2>Choose an organisation</h2>
        <p>Select an organisation to see the costs that are due but not yet billed.</p>
      </div>
    );
  }
  const { status, items, total, limit } = pending;
  const loadingFirst = status === 'loading' && !items.length;
  const shownAll = items.length >= total;
  return (
    <div className="teacher-ledger-pending" data-testid="teacher-ledger-pending">
      {loadingFirst ? <p className="teacher-ledger-loading" role="status">Loading your pending items…</p> : null}
      {status === 'error' ? (
        <div className="teacher-ledger-state teacher-ledger-state-failed">
          <h2>Your pending items could not be loaded</h2>
          <p role="alert">{pending.message}</p>
          <button type="button" onClick={onRetry}>Try again</button>
        </div>
      ) : null}
      {status === 'ready' && !items.length ? (
        <div className="teacher-ledger-state" role="status">
          <h2>Nothing is waiting to be billed</h2>
          <p>Costs that are due but not yet billed appear here. Billed history is in the Billed tab.</p>
        </div>
      ) : null}
      {items.length ? (
        <>
          <p className="teacher-ledger-pending-count" data-testid="teacher-ledger-pending-count" role="status">
            {shownAll ? `${total} pending item${total === 1 ? '' : 's'}` : `Showing ${items.length} of ${total} pending items`}
          </p>
          <table>
            <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Stage</th><th>Status</th></tr></thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-testid="teacher-ledger-pending-row">
                  <td>{pendingWhen(item)}</td>
                  <td>{pendingDescription(item)}</td>
                  <td>{pendingAmount(item)}</td>
                  <td>{stageWords(item.stage)}</td>
                  <td>{stateWords(item.state)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!shownAll && limit < PENDING_MAX ? (
            <button type="button" onClick={onShowMore} disabled={status === 'loading'}>Show more</button>
          ) : null}
          {!shownAll && limit >= PENDING_MAX ? (
            <p role="status">Only the first {items.length} are listed here; {total - items.length} more are not shown.</p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const TeacherLedgerPage = () => {
  const { user } = useAuth();
  const [organisations, setOrganisations] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [tab, setTab] = useState('pending');
  const [account, setAccount] = useState(null);
  const [ledgerStatus, setLedgerStatus] = useState('idle');
  const [ledgerMessage, setLedgerMessage] = useState('');
  const [resolveError, setResolveError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  // M004-F0702: the caller's own pending list (independent of the account read).
  const [pending, setPending] = useState({ status: 'idle', items: [], total: 0, limit: PENDING_PAGE, message: '' });
  const [pendingLimit, setPendingLimit] = useState(PENDING_PAGE);
  const [pendingReload, setPendingReload] = useState(0);

  useEffect(() => {
    let current = true;
    Promise.all([organisationApi.getMine(), userApi.getApplicationAccess()])
      .then(([mineResponse, accessResponse]) => {
        if (!current) return;
        const ledgerOrganisations = selectTeacherLedgerOrganisations(mineResponse, accessResponse);
        setOrganisations(ledgerOrganisations);
        if (ledgerOrganisations.length === 1) setSelectedOrgId(String(ledgerOrganisations[0].id));
      })
      .catch((cause) => {
        if (current) setResolveError(apiMessage(cause) || 'StudioAEye could not load your Ledger organisations.');
      });
    return () => { current = false; };
  }, []);

  useEffect(() => {
    if (!selectedOrgId || !user?.id) return undefined;
    let current = true;
    setLedgerStatus('loading');
    setLedgerMessage('');
    ledgerAccountApi.getAccount(selectedOrgId, String(user.id))
      .then((response) => {
        if (!current) return;
        setAccount(payload(response));
        setLedgerStatus('ready');
      })
      .catch((cause) => {
        if (!current) return;
        setAccount(null);
        if (isLedgerOfflineError(cause)) {
          setLedgerStatus('offline');
          setLedgerMessage(apiMessage(cause));
        } else {
          setLedgerStatus('error');
          setLedgerMessage(apiMessage(cause) || 'Your Ledger could not be loaded.');
        }
      });
    return () => { current = false; };
  }, [selectedOrgId, user?.id, reloadToken]);

  useEffect(() => {
    setPendingLimit(PENDING_PAGE);
  }, [selectedOrgId]);

  useEffect(() => {
    if (!selectedOrgId) {
      setPending({ status: 'idle', items: [], total: 0, limit: PENDING_PAGE, message: '' });
      return undefined;
    }
    let current = true;
    // Keep the rows already on screen while a larger page loads.
    setPending((previous) => ({ ...previous, status: 'loading', message: '' }));
    ledgerAccountApi.getOwnPending(selectedOrgId, pendingLimit)
      .then((response) => {
        if (!current) return;
        setPending({ status: 'ready', ...normalisePending(response), message: '' });
      })
      .catch((cause) => {
        if (!current) return;
        setPending({ status: 'error', items: [], total: 0, limit: pendingLimit, message: apiMessage(cause) || 'Your pending items could not be loaded.' });
      });
    return () => { current = false; };
  }, [selectedOrgId, pendingLimit, pendingReload]);

  const retry = useCallback(() => setReloadToken((token) => token + 1), []);
  const retryPending = useCallback(() => setPendingReload((token) => token + 1), []);
  const offline = ledgerStatus === 'offline';
  const failed = ledgerStatus === 'error';
  const loading = ledgerStatus === 'loading';

  return (
    <Layout>
      <div className="teacher-ledger-page">
        <header className="teacher-ledger-heading">
          <span>Ledger</span>
          <h1>Ledger</h1>
          <p>Costs that are due but not yet billed, and the billed history your organisation&rsquo;s Ledger keeps.</p>
        </header>

        {resolveError ? <p className="teacher-ledger-error" role="alert">{resolveError}</p> : null}

        {organisations !== null && organisations.length === 0 ? (
          <div className="teacher-ledger-state" role="status">
            <h2>No Ledger organisation</h2>
            <p>Your account does not have Ledger access for an organisation yet. Ask your studio administrator to enable it.</p>
          </div>
        ) : null}

        {(organisations === null || organisations.length > 0) ? (
          <>
            <div className="teacher-ledger-controls">
              {organisations === null ? (
                <p className="teacher-ledger-org-name">Resolving your organisations…</p>
              ) : organisations.length > 1 ? (
                <label className="teacher-ledger-org-picker">
                  Organisation
                  <select value={selectedOrgId} onChange={(event) => setSelectedOrgId(event.target.value)}>
                    {organisations.map((organisation) => (
                      <option key={organisation.id} value={String(organisation.id)}>{organisation.name}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <p className="teacher-ledger-org-name">{organisationName(organisations[0])}</p>
              )}
              <div className="teacher-ledger-tabs" role="tablist" aria-label="Ledger views">
                <button type="button" role="tab" aria-selected={tab === 'pending'} className={tab === 'pending' ? 'active' : ''} onClick={() => setTab('pending')}>Pending</button>
                <button type="button" role="tab" aria-selected={tab === 'billed'} className={tab === 'billed' ? 'active' : ''} onClick={() => setTab('billed')}>Billed</button>
              </div>
            </div>

            <section className="teacher-ledger-panel" role="tabpanel" aria-label={tab === 'pending' ? 'Pending costs' : 'Billed history'}>
              {loading && tab === 'billed' ? <p className="teacher-ledger-loading" role="status">Loading your Ledger…</p> : null}

              {offline ? (
                <div className="teacher-ledger-state" data-testid="teacher-ledger-offline" role="status">
                  <h2>Ledger is offline</h2>
                  <p>{ledgerMessage || 'Your organisation’s Ledger node is not reachable right now. Billed history and invoices reappear when it reconnects.'}</p>
                  <button type="button" onClick={retry}>Try again</button>
                </div>
              ) : null}

              {failed ? (
                <div className="teacher-ledger-state teacher-ledger-state-failed">
                  <h2>Your Ledger could not be loaded</h2>
                  <p role="alert">{ledgerMessage}</p>
                  <button type="button" onClick={retry}>Try again</button>
                </div>
              ) : null}

              {tab === 'pending' ? (
                <PendingList
                  pending={pending}
                  hasOrganisation={Boolean(selectedOrgId)}
                  onRetry={retryPending}
                  onShowMore={() => setPendingLimit((limit) => nextLimit(limit))}
                />
              ) : null}

              {tab === 'billed' && !loading && !offline && !failed ? (
                (
                  account ? (
                    <div className="teacher-ledger-billed">
                      <div className="teacher-ledger-figures">
                        <div><span>Balance</span><strong>{money(account.balance)}</strong></div>
                        <div><span>Credits remaining</span><strong>{money(account.credits_remaining)}</strong></div>
                        <div><span>Invoices</span><strong>{(account.invoices || []).length}</strong></div>
                      </div>
                      <section className="teacher-ledger-section">
                        <h3>Billed history</h3>
                        {account.entries?.length ? (
                          <table>
                            <thead><tr><th>Ledger entry</th><th>TX</th><th>Description</th><th>Amount</th><th>Date</th></tr></thead>
                            <tbody>
                              {account.entries.map((entry) => (
                                <tr key={entry.transaction_id}>
                                  <td>{entry.transaction_id}</td>
                                  {/* M004-F0712 S8: a teacher is not a billing viewer: no orgId, so the chip is copy-only. */}
                                  <td><TxChip handOffId={entry.hand_off_id} /></td>
                                  <td>{entry.description}</td>
                                  <td>{money(entry.amount, entry.currency)}</td>
                                  <td>{entryDate(entry)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : <p>No transactions have been posted.</p>}
                      </section>
                    </div>
                  ) : (
                    <div className="teacher-ledger-state" role="status">
                      <h2>No billed history yet</h2>
                      <p>Your organisation’s Ledger has not returned any account data for you yet.</p>
                    </div>
                  )
                )
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </Layout>
  );
};

export default TeacherLedgerPage;
